import React, { useState, useEffect, useMemo } from "react";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Box, Tooltip, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { DateRangePickerDay } from "@mui/x-date-pickers-pro/DateRangePickerDay";

// Styled Day Component to override colors
const StyledDateRangePickerDay = styled(DateRangePickerDay)(({ theme }) => ({
  // "&.MuiDateRangePickerDay-dayInsideRange": {
  //   backgroundColor: "rgba(255, 179, 71, 0.25) !important", // Lighter #FFB347 for range
  // },
  // "&.MuiDateRangePickerDay-rangeIntervalPreview": {
  //   backgroundColor: "rgba(255, 179, 71, 0.25) !important",
  // },
  // "& .MuiPickersDay-root.Mui-selected": {
  //   backgroundColor: "#FFB347 !important", // Main #FFB347 for start/end circles
  //   "&:hover": {
  //     backgroundColor: "#E59F3E !important",
  //   },
  // },
}));


// const StyledDateRangePickerDay = styled(DateRangePickerDay)(({ theme }) => ({
//   /* ===== Middle days (range background) ===== */
//   "&.MuiDateRangePickerDay-dayInsideRange": {
//     backgroundColor: "rgba(255, 179, 71, 0.25)", // light amber
//     color: "#000",
//   },

//   "&.MuiDateRangePickerDay-dayOutsideMonth.MuiDateRangePickerDay-dayInsideRange": {
//     backgroundColor: "transparent",
//   },

//   /* ===== Start & End day circles ===== */
//   "&.MuiDateRangePickerDay-rangeIntervalDayHighlight": {
//     backgroundColor: "#f7cd93ff", // main selected color
//     color: "#000",
//   },

//   /* ===== Hover / drag preview ===== */
//   "&.MuiDateRangePickerDay-rangeIntervalPreview": {
//     backgroundColor: "rgba(255, 179, 71, 0.18)",
//   },

//   /* ===== Ensure today dot doesn't override ===== */
//   "&.MuiPickersDay-today": {
//     border: "none",
//   },
//   "& .MuiPickersDay-root.Mui-selected": {
//     backgroundColor: "#FFB347 !important", // Main #FFB347 for start/end circles
//     "&:hover": {
//       backgroundColor: "#E59F3E !important",
//     },
//   },
// }));




// const StyledDateRangePickerDay = styled(DateRangePickerDay)(({ theme }) => ({
//   /* ===== Middle days (range background) ===== */
//   "&.MuiDateRangePickerDay-dayInsideRange": {
//     backgroundColor: "rgba(255, 179, 71, 0.25)", // light amber
//     color: "#000",
//   },

//   /* ===== Start & End day circles ===== */
//   "&.MuiDateRangePickerDay-rangeIntervalDayHighlight": {
//     backgroundColor: "#ffc97d", // main selected color
//     color: "#000",
//     // borderRadius: "50%",

//     "&:hover": {
//       backgroundColor: "#E59F3E",

//     },
//   },

//   /* ===== Hover / drag preview ===== */
//   "&.MuiDateRangePickerDay-rangeIntervalPreview": {
//     backgroundColor: "rgba(255, 179, 71, 0.18)",
//   },

//   /* ===== Ensure today dot doesn't override ===== */
//   "&.MuiPickersDay-today": {
//     border: "none",
//   },
// }));


// Custom TextField with Tooltip
const TooltipTextField = React.forwardRef((props, ref) => {
  const { tooltipTitle, ...other } = props;
  return (
    <Tooltip title={tooltipTitle || ""} arrow placement="bottom">
      <TextField {...other} ref={ref} />
    </Tooltip>
  );
});

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

function ADDateRange({ onDateChange, defaultDays = 14, value }) {
  const tz = dayjs.tz.guess(); // Get local timezone

  // ✅ DERIVED STATE: Convert parent [Date, Date] prop to [Dayjs, Dayjs]
  // This serves as the "Source of Truth" from the parent.
  const controlledValue = useMemo(() => {
    if (value && value[0] && value[1]) {
      return [dayjs(value[0]).tz(tz), dayjs(value[1]).tz(tz)];
    }
    // Fallback if parent hasn't initialized yet
    return [
      dayjs().tz(tz).subtract(defaultDays, "day").startOf("day"),
      dayjs().tz(tz).endOf("day"),
    ];
  }, [value, tz, defaultDays]);

  // ✅ LOCAL STATE: Handles incomplete selections (dragging/partial)
  const [internalRange, setInternalRange] = useState(controlledValue);

  // ✅ SYNC EFFECT: Whenever parent updates (or rejects change), force local state to match
  useEffect(() => {
    setInternalRange(controlledValue);
  }, [controlledValue]);

  const handleDateRangeChange = (newRange) => {
    // Optimistically update local UI so user sees their selection
    setInternalRange(newRange);

    // Only notify parent if selection is complete [start, end]
    if (newRange[0] && newRange[1]) {
      onDateChange?.(newRange);
    }
  };

  // ✅ REVERT ON CLOSE: If picker closes and parent rejected the change, 
  // this snaps the local state back to the parent's actual value.
  const handleClose = () => {
    setInternalRange(controlledValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" flexDirection="column" gap={0}>
        <MDTypography
          variant="button"
          fontWeight="medium"
          color="text"
          sx={{ marginBottom: 1, display: "block", textAlign: "center", }}
        >
          Date Selector
        </MDTypography>
        <MDBox
          bgColor="#f7f7f7" // light grey background
          sx={{

            borderRadius: "16px",
            backgroundColor: "#f0f2f5",
            padding: "14px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <DateRangePicker
            value={internalRange}
            onChange={handleDateRangeChange}
            onClose={handleClose} // ✅ Ensures revert if rejected
            format="DD-MMM-YYYY"
            // localeText={{ start: "From", end: "To" }}
            localeText={{ start: "", end: "" }}
            sx={{
              "& .MuiDateRangePickerInput-root": {
                width: "auto",
                gap: 0.5,
              },
              "& .MuiTextField-root": {
                width: 125,
              },
              "& .MuiOutlinedInput-root": {
                height: 48, // ✅ match chart card height
                borderRadius: "12px",
                backgroundColor: "grey.100", // ✅ same grey background
                boxShadow: 1, // ✅ subtle shadow like unselected card
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4, // same hover shadow effect
                  cursor: "pointer", // ✅ Pointer on hover
                },
                "&.Mui-focused": {
                  boxShadow: 6, // highlight when focused
                  //border: "2px solid #00A1A1", // teal border like selected card
                  backgroundColor: "grey.400",
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: "12px 14px",
                textAlign: "center",
                color: "black",
                fontWeight: 500,
                cursor: "pointer", // ✅ Force pointer (overrides text cursor)
              },
            }}

            slots={{
              textField: TooltipTextField,
              day: StyledDateRangePickerDay
            }}
            slotProps={{
              textField: (ownerState) => ({
                tooltipTitle: ownerState.position === 'start' ? "Start Date" : "End Date"
              }),

              popper: {
                placement: "bottom-start", // ✅ Force bottom-right alignment
                modifiers: [
                  {
                    name: "flip",
                    enabled: false, // ✅ Prevent left-side flip
                  },
                  {
                    name: "preventOverflow",
                    options: {
                      altAxis: true,
                      tether: false,
                    },
                  },
                ],
              },
              shortcuts: {
                items: [
                  {
                    label: "Current Week",
                    getValue: () => [
                      dayjs().tz(tz).startOf("week"),
                      dayjs().tz(tz).endOf("week"),
                    ],
                  },
                  {
                    label: "Previous Week",
                    getValue: () => [
                      dayjs().tz(tz).subtract(1, "week").startOf("week"),
                      dayjs().tz(tz).subtract(1, "week").endOf("week"),
                    ],
                  },
                  {
                    label: "Current Month",
                    getValue: () => [
                      dayjs().tz(tz).startOf("month"),
                      dayjs().tz(tz).endOf("month"),
                    ],
                  },
                  {
                    label: "Previous Month",
                    getValue: () => [
                      dayjs().tz(tz).subtract(1, "month").startOf("month"),
                      dayjs().tz(tz).subtract(1, "month").endOf("month"),
                    ],
                  },
                ],

                sx: {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center", // ✅ Force strict full width
                  justifyContent: "center",
                  gap: 1.5,
                  padding: "16px",
                  "& .MuiButtonBase-root": { // ✅ Target MUI buttons specifically
                    width: "100% !important", // ✅ Force full width
                    maxWidth: "none !important", // ✅ Remove any MUI constraints
                    textAlign: "center",
                    fontSize: "0.875rem",
                    borderRadius: "8px",
                    backgroundColor: "#f1f1f1",
                    color: "#333",
                    "&:hover": {
                      backgroundColor: "#e0e0e0",
                    },
                  },
                },


              },
            }}
            defaultCalendarMonth={dayjs().tz(tz).subtract(1, "month")}
          />
        </MDBox>
      </Box>
    </LocalizationProvider>
  );
}

ADDateRange.propTypes = {
  onDateChange: PropTypes.func,
  defaultDays: PropTypes.number,
  value: PropTypes.array // [Date, Date]
};

export default ADDateRange;
